import React, { useContext, useState, useCallback, useMemo } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import jwtDecode from "jwt-decode";

import { GOOGLE_CLIENT_ID } from "environment";

import { AuthenticationService } from "../services";
import { useCookies } from "../../../shared/hooks";
import { AdminRole, SuperAdminRole, AccountantRole, VisitorRole } from "../constants/roleConstants";

const adminRoles = [AdminRole, SuperAdminRole, AccountantRole];

const AuthenticationContext = React.createContext();

export function useAuthentication() {
  return useContext(AuthenticationContext);
}

export function AuthenticationProvider({ children }) {
  const { getAccessToken } = useCookies(children);

  const [user, setUser] = useState(() => {
    try {
      return jwtDecode(getAccessToken())?.googleUser;
    } catch {
      return undefined;
    }
  });

  const [role, setRole] = useState(() => {
    try {
      return jwtDecode(getAccessToken())?.role;
    } catch {
      return undefined;
    }
  });

  const isVisitor = useMemo(() => role === VisitorRole, [role]);
  const isPrivileged = useMemo(() => adminRoles.some((adminRole) => adminRole === role), [role]);

  const signIn = useCallback(({ credential }) => {
    AuthenticationService.logIn(credential).then((response) => {
      setUser(response.googleUser);
      setRole(response.role);
    });
  }, []);

  const signOut = useCallback(() => {
    AuthenticationService.logOut().then(() => {
      setUser();
      setRole();
    });
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthenticationContext.Provider value={{ user, signIn, signOut, role, isVisitor, isPrivileged }}>
        {children}
      </AuthenticationContext.Provider>
    </GoogleOAuthProvider>
  );
}
